import React, { useState } from 'react';
import { vehicleApi } from '../../../api/vehicleApi';
import { useSafeClose } from '../../../hooks/useSafeClose';
import './CategoryManagerModal.css';

export default function CategoryManagerModal({ isOpen, onClose, categories = [], onUpdate }) {
    const [editingCategory, setEditingCategory] = useState(null);
    const [name, setName] = useState('');
    const [color, setColor] = useState('#64748b');
    const [loading, setLoading] = useState(false);

    const { overlayRef, onMouseDown, onMouseUp } = useSafeClose(onClose);

    if (!isOpen) return null;

    const handleSave = async (e) => {
        e.preventDefault();
        const trimmedName = name.trim();
        if (!trimmedName) return;

        setLoading(true);
        try {
            if (editingCategory) {
                await vehicleApi.updateCategory(editingCategory.id, { name: trimmedName, color });
            } else {
                await vehicleApi.createCategory({ name: trimmedName, color });
            }
            setName('');
            setColor('#64748b');
            setEditingCategory(null);
            if (onUpdate) onUpdate();
        } catch (err) {
            console.error('Error saving category:', err);
            const msg = err.response?.data?.error || err.message || 'Unbekannter Fehler beim Speichern';
            alert(`Fehler beim Speichern des Typs: ${msg}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const { count } = await vehicleApi.getCategoryVehicleCount(id);
            const message = count > 0 
                ? `Fahrzeugtyp wirklich löschen? Es sind ${count} Fahrzeuge betroffen, die dann auf 'Kein Typ' gesetzt werden.`
                : 'Fahrzeugtyp wirklich löschen?';
            
            if (!window.confirm(message)) return;
            
            await vehicleApi.deleteCategory(id);
            onUpdate();
        } catch (err) {
            alert('Fehler beim Löschen');
        }
    };

    const startEdit = (cat) => {
        setEditingCategory(cat);
        setName(cat.name);
        setColor(cat.color || '#64748b');
    };

    const cancelEdit = () => {
        setEditingCategory(null);
        setName('');
        setColor('#64748b');
    };

    return (
        <div className="modal-overlay" ref={overlayRef} onMouseDown={onMouseDown} onMouseUp={onMouseUp}>
            <div className="category-manager-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>🏷️ Typen verwalten</h3>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>

                <div className="modal-body">
                    <form className="category-form" onSubmit={handleSave}>
                        <div className="form-row">
                            <div className="form-group" style={{ flex: 1 }}>
                                <label>Bezeichnung</label>
                                <input 
                                    type="text" 
                                    value={name} 
                                    onChange={e => setName(e.target.value)} 
                                    placeholder="z.B. LKW"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Farbe</label>
                                <div className="color-input-wrapper">
                                    <input 
                                        type="color" 
                                        value={color} 
                                        onChange={e => setColor(e.target.value)}
                                        className="color-input"
                                    />
                                </div>
                            </div>
                            <div className="form-group" style={{ display: 'flex', gap: '8px' }}>
                                <button type="submit" className="btn-primary" disabled={loading}>
                                    {editingCategory ? 'Speichern' : 'Hinzufügen'}
                                </button>
                                {editingCategory && (
                                    <button type="button" className="btn-secondary" onClick={cancelEdit}>
                                        Abbrechen
                                    </button>
                                )}
                            </div>
                        </div>
                    </form>

                    <div className="category-list">
                        {categories.length === 0 ? (
                            <div className="empty-state">
                                <span>📂</span>
                                <p>Noch keine Fahrzeugtypen angelegt.</p>
                            </div>
                        ) : (
                            categories.map(cat => (
                                <div key={cat.id} className="category-item">
                                    <div className="category-info">
                                        <div className="color-dot" style={{ backgroundColor: cat.color }} />
                                        <span className="category-name">{cat.name}</span>
                                    </div>
                                    <div className="category-actions">
                                        <button onClick={() => startEdit(cat)} title="Bearbeiten">
                                            📝
                                        </button>
                                        <button onClick={() => handleDelete(cat.id)} className="delete" title="Löschen">
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
